// =====================================================================
// Forecast — Per-rep submission, team review, and management overview.
//
// Three tabs:
//   - Overview: headline KPIs, forecast-vs-quota waterfall, pipeline
//     mix, MEDDPICCR distribution, attainment leaderboard, deals at risk
//   - Deals:    flat table of every open opportunity
//   - Team:     per-rep summary with drill-down into each rep's pipeline
//
// Split out from sales-misc.jsx so each page lives in its own file.
// =====================================================================

// Shared deals table used by the Deals tab and by the Team-tab drill-
// down panel. Clicking a row navigates to the opportunity detail.
function ForecastDealsTable({ rows, navigate, emptyText }) {
  if (!rows.length) {
    return <div className="card-body muted">{emptyText || "No open deals."}</div>;
  }
  return (
    <table className="tbl tbl-flush">
      <thead><tr>
        <th>Name</th><th>Account</th><th>Stage</th>
        <th className="right">ACV</th><th>Close date</th>
        <th>Forecast</th><th>MEDDPICCR</th><th>Owner</th>
        <th className="right">Days in stage</th>
      </tr></thead>
      <tbody>
        {rows.map(o => {
          const a = getAccount(o.accountId) || {};
          const score = meddScore(o);
          return (
            <tr key={o.id}
                style={{ cursor: "pointer" }}
                onClick={() => navigate && navigate("sales/opportunities/" + o.id)}>
              <td><span className="tbl-link">{o.name}</span></td>
              <td>{a.name || <span className="muted">—</span>}</td>
              <td><StageBadge stage={o.stage} /></td>
              <td className="right numeric">{fmtMoney(o.acv, o.currency)}</td>
              <td>{fmtDateShort(o.closeDate)}{o.pushCount > 0 && <span className="muted"> ({o.pushCount}×)</span>}</td>
              <td><ForecastBadge cat={o.forecastCategory} /></td>
              <td><span className="numeric">{score.got}/{score.total}</span> <MeddPip opp={o} /></td>
              <td>{o.owner ? <Avatar user={o.owner} size={20} /> : <span className="muted">—</span>}</td>
              <td className="right numeric">{o.daysInStage != null ? o.daysInStage + "d" : <span className="muted">—</span>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Management-level visuals for the Forecast page's Overview tab. Pulled
// out of ForecastPage so the page body stays readable.
function ForecastOverviewTab({ navigate, reps, openOpps }) {
  // Per-rep totals shared by the headline KPIs and the leaderboard.
  const perRep = reps.map(u => {
    const opps   = openOpps.filter(o => o.owner === u.id);
    const commit = opps.filter(o => o.forecastCategory === "Commit").reduce((a,o) => a + (o.acv || 0), 0);
    const best   = opps.filter(o => o.forecastCategory === "Best case").reduce((a,o) => a + (o.acv || 0), 0);
    const pipe   = opps.filter(o => o.forecastCategory === "Pipeline").reduce((a,o) => a + (o.acv || 0), 0);
    // Match the Team tab: closed-won is estimated from attainment × quota × 0.6.
    const cw     = Math.round((u.attainment || 0) * (u.quota || 0) * 0.6);
    return { u, opps, commit, best, pipe, cw };
  });

  const totalQuota  = reps.reduce((a, u) => a + (u.quota || 0), 0);
  const totalCW     = perRep.reduce((a, r) => a + r.cw, 0);
  const totalCommit = perRep.reduce((a, r) => a + r.commit, 0);
  const totalBest   = perRep.reduce((a, r) => a + r.best, 0);
  const totalPipe   = perRep.reduce((a, r) => a + r.pipe, 0);

  const projected   = totalCW + totalCommit;            // landing forecast
  const upside      = projected + totalBest;            // with best-case included
  const gapToQuota  = Math.max(0, totalQuota - projected);
  const coverage    = totalCommit > 0 ? totalPipe / totalCommit : 0;

  // Pipeline by stage — count + ACV per stage, excluding empty ones.
  const STAGES_ARR = D.STAGES || [];
  const stageRows = STAGES_ARR
    .filter(s => !s.startsWith("Closed"))
    .map(s => {
      const inStage = openOpps.filter(o => o.stage === s);
      return { stage: s, count: inStage.length, acv: inStage.reduce((a,o) => a + (o.acv || 0), 0) };
    })
    .filter(r => r.count > 0);
  const maxStageCount = Math.max(1, ...stageRows.map(r => r.count));

  // MEDDPICCR completeness distribution across open deals.
  const meddTotal = (D.MEDDPICCR_KEYS || []).length || 9;
  const meddDist  = Array.from({ length: meddTotal + 1 }, (_, i) =>
    openOpps.filter(o => meddScore(o).got === i).length);
  const maxMedd   = Math.max(1, ...meddDist);
  const thresholds = window.tpThresholds.meddpiccForecastThresholds(D.SETTINGS);
  const meddReady = openOpps.filter(o => meddScore(o).got >= thresholds.commit).length;

  // Forecast-mix bar scale uses the largest of the three buckets so
  // small categories don't collapse to invisible.
  const fcMax = Math.max(1, totalCommit, totalBest, totalPipe) / 1000;

  // Deals at risk — at least one of: stalled (>60d in stage), pushed
  // (closeDate moved twice), low MEDDPICCR (<4 of 9). Sorted by closeDate
  // so the most time-sensitive ones surface first.
  const atRisk = openOpps
    .map(o => {
      const reasons = [];
      if ((o.daysInStage || 0) > 60) reasons.push("stalled " + o.daysInStage + "d");
      if ((o.pushCount || 0) >= 2)   reasons.push("pushed " + o.pushCount + "×");
      if (meddScore(o).got < thresholds.atRiskBelow) reasons.push("MEDDPICCR " + meddScore(o).got + "/" + meddTotal);
      return { o, reasons };
    })
    .filter(x => x.reasons.length > 0)
    .sort((a, b) => (a.o.closeDate || "").localeCompare(b.o.closeDate || ""))
    .slice(0, 6);

  // Attainment leaderboard — projected (cw + commit) as % of quota.
  const leaderboard = perRep
    .map(r => ({
      ...r,
      projected: r.cw + r.commit,
      pct: r.u.quota ? (r.cw + r.commit) / r.u.quota : 0,
    }))
    .sort((a, b) => b.pct - a.pct);

  return (
    <>
      {/* Row 1 — headline KPIs */}
      <div className="grid grid-4 mt-8">
        <Stat label="Team quota"      value={fmtMoney(totalQuota)} />
        <Stat label="Closed Won"      value={fmtMoney(totalCW)}
              delta={totalQuota ? Math.round(totalCW / totalQuota * 100) + "% to quota" : null} />
        <Stat label="Commit (ACV)"    value={fmtMoney(totalCommit)}
              delta={totalQuota ? Math.round((totalCW + totalCommit) / totalQuota * 100) + "% projected" : null} />
        <Stat label="Best case (ACV)" value={fmtMoney(totalBest)}
              delta={openOpps.length + " open deals"} />
      </div>

      {/* Row 2 — Forecast vs quota waterfall */}
      <div className="card mt-16">
        <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div className="card-title">Forecast vs quota</div>
            <div className="card-sub">Closed Won + Commit projected vs team quota · best case adds upside</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="numeric" style={{ fontSize: 18, fontWeight: 600 }}>
              {totalQuota ? Math.round(projected / totalQuota * 100) + "%" : "—"}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>to quota (projected)</div>
          </div>
        </div>
        <div className="card-body">
          <BarRow label="Closed Won" value={Math.round(totalCW / 1000)}     max={Math.max(1, totalQuota / 1000)} tone="bar-green" />
          <BarRow label="Commit"     value={Math.round(totalCommit / 1000)} max={Math.max(1, totalQuota / 1000)} tone="bar-blue" />
          <BarRow label="Best case"  value={Math.round(totalBest / 1000)}   max={Math.max(1, totalQuota / 1000)} tone="" />
          <BarRow label="Quota"      value={Math.round(totalQuota / 1000)}  max={Math.max(1, totalQuota / 1000)} tone="bar-slate" />
          <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
            Gap to quota: <b>{fmtMoney(gapToQuota)}</b>
            {" · "}With upside: <b>{fmtMoney(upside)}</b> ({totalQuota ? Math.round(upside / totalQuota * 100) + "%" : "—"})
            {" · "}Pipeline coverage: <b>{coverage > 0 ? coverage.toFixed(1) + "×" : "—"}</b> commit
          </div>
        </div>
      </div>

      {/* Row 3 — pipeline by stage / forecast mix / MEDDPICCR */}
      <div className="grid grid-3 mt-16" style={{ gridTemplateColumns: "1.5fr 1fr 1fr" }}>
        <div className="card">
          <div className="card-head">
            <div className="card-title">Pipeline by stage</div>
            <div className="card-sub">{openOpps.length} open · {fmtMoney(totalCommit + totalBest + totalPipe)} ACV</div>
          </div>
          {stageRows.length === 0 ? (
            <div className="card-body muted">No open opportunities.</div>
          ) : (
            <div className="card-body">
              {stageRows.map(r => (
                <BarRow key={r.stage} label={r.stage} value={r.count} max={maxStageCount} />
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Forecast mix</div></div>
          <div className="card-body">
            <BarRow label="Commit"    value={Math.round(totalCommit / 1000)} max={fcMax} tone="bar-green" />
            <BarRow label="Best case" value={Math.round(totalBest / 1000)}   max={fcMax} tone="" />
            <BarRow label="Pipeline"  value={Math.round(totalPipe / 1000)}   max={fcMax} tone="bar-slate" />
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              In $k. Commit + Best case = <b>{Math.round((totalCommit + totalBest) / 1000)}k</b> potential close.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">MEDDPICCR distribution</div>
            <div className="card-sub">across {openOpps.length} open deals</div>
          </div>
          <div className="card-body">
            {meddDist.map((n, i) => (
              <BarRow key={i} label={i + " / " + meddTotal} value={n} max={maxMedd} />
            ))}
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              <b>{meddReady}</b> deal{meddReady === 1 ? "" : "s"} meet the Commit threshold (≥{Math.ceil(meddTotal * 2 / 3)} elements).
            </div>
          </div>
        </div>
      </div>

      {/* Row 4 — Attainment leaderboard + Deals at risk */}
      <div className="grid grid-2 mt-16">
        <div className="card">
          <div className="card-head">
            <div className="card-title">Attainment leaderboard</div>
            <div className="card-sub">Closed Won + Commit ÷ quota · ranked</div>
          </div>
          {leaderboard.length === 0 ? (
            <div className="card-body muted">No reps with quota.</div>
          ) : (
            <table className="tbl tbl-flush">
              <thead><tr>
                <th>Rep</th><th>Team</th>
                <th className="right">Quota</th>
                <th className="right">Projected</th>
                <th>Attainment</th>
              </tr></thead>
              <tbody>
                {leaderboard.map(r => (
                  <tr key={r.u.id} style={{ cursor: "pointer" }}>
                    <td><span className="flex-gap" style={{ alignItems: "center" }}><Avatar user={r.u} size={22} /> {r.u.name}</span></td>
                    <td className="muted">{r.u.team}</td>
                    <td className="right numeric">{fmtMoney(r.u.quota)}</td>
                    <td className="right numeric">{fmtMoney(r.projected)}</td>
                    <td>
                      <Badge tone={r.pct >= 1 ? "green" : r.pct >= 0.7 ? "amber" : "red"}>
                        {Math.round(r.pct * 100)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Deals at risk</div>
            <div className="card-sub">Stalled, repeatedly pushed, or low MEDDPICCR · click to open</div>
          </div>
          {atRisk.length === 0 ? (
            <div className="card-body muted">No at-risk deals right now.</div>
          ) : (
            <div className="card-body" style={{ padding: 0 }}>
              {atRisk.map(({ o, reasons }) => {
                const a = getAccount(o.accountId) || {};
                return (
                  <div key={o.id} className="flex-between"
                       style={{ padding: "10px 16px", borderBottom: "1px solid var(--divider)", cursor: "pointer" }}
                       onClick={() => navigate && navigate("sales/opportunities/" + o.id)}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.name}</div>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        {a.name || "—"} · {fmtMoney(o.acv, o.currency)} · close {fmtDateShort(o.closeDate)}
                      </div>
                      <div style={{ fontSize: 11.5, marginTop: 2, color: "var(--red)" }}>{reasons.join(" · ")}</div>
                    </div>
                    <div style={{ marginLeft: 12 }}><ForecastBadge cat={o.forecastCategory} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ForecastPage({ navigate }) {
  const [tab, setTab] = useState("overview");
  const [selectedRep, setSelectedRep] = useState(null);

  const reps = assignableReps("AE");
  const openOpps = D.OPPORTUNITIES.filter(o => !(o.stage || "").startsWith("Closed"));

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "deals",    label: "Deals", count: openOpps.length },
    { key: "team",     label: "Team",  count: reps.length },
  ];

  // Clear the team-tab drill-down whenever we switch tabs so reopening
  // the Team tab starts from the clean summary view.
  const switchTab = (k) => { setTab(k); setSelectedRep(null); };

  const selectedUser = selectedRep ? getUser(selectedRep) : null;
  const selectedRepOpps = selectedRep ? openOpps.filter(o => o.owner === selectedRep) : [];

  return (
    <>
      <PageHead title="Forecast" sub="Per-rep, per-team submission and review · Q2 2026" />
      <Tabs tabs={tabs} current={tab} onChange={switchTab} />

      {tab === "overview" && (
        <ForecastOverviewTab navigate={navigate} reps={reps} openOpps={openOpps} />
      )}

      {tab === "team" && (
        <>
          <div className="card"><table className="tbl tbl-flush">
            <thead><tr>
              <th>Rep</th><th>Team</th>
              <th className="right">Quota</th><th className="right">Closed Won</th>
              <th className="right">Commit</th><th className="right">Best case</th>
              <th className="right">Pipeline</th><th className="right">Attainment</th>
              <th>Override</th>
            </tr></thead>
            <tbody>{reps.map(u => {
              const opps   = openOpps.filter(o => o.owner === u.id);
              const commit = opps.filter(o => o.forecastCategory === "Commit").reduce((a,o) => a + (o.acv || 0), 0);
              const best   = opps.filter(o => o.forecastCategory === "Best case").reduce((a,o) => a + (o.acv || 0), 0);
              const pipe   = opps.filter(o => o.forecastCategory === "Pipeline").reduce((a,o) => a + (o.acv || 0), 0);
              const cw     = Math.round((u.attainment || 0) * (u.quota || 0) * 0.6);
              const sel    = selectedRep === u.id;
              return (
                <tr key={u.id}
                    style={{ cursor: "pointer", background: sel ? "var(--bg-hover)" : undefined }}
                    onClick={() => setSelectedRep(sel ? null : u.id)}>
                  <td><span className="flex-gap" style={{alignItems:"center"}}><Avatar user={u} size={22} /> {u.name}</span></td>
                  <td className="muted">{u.team}</td>
                  <td className="right numeric">{fmtMoney(u.quota)}</td>
                  <td className="right numeric">{fmtMoney(cw)}</td>
                  <td className="right numeric">{fmtMoney(commit)}</td>
                  <td className="right numeric">{fmtMoney(best)}</td>
                  <td className="right numeric">{fmtMoney(pipe)}</td>
                  <td className="right"><Badge tone={u.attainment >= 1 ? "green" : u.attainment >= 0.7 ? "amber" : "red"}>{Math.round(u.attainment*100)}%</Badge></td>
                  <td><span className="muted">—</span></td>
                </tr>
              );
            })}</tbody>
          </table></div>

          {selectedUser && (
            <div className="card" style={{ marginTop: 12 }}>
              <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div className="card-title">{selectedUser.name}'s pipeline</div>
                  <div className="card-sub">{selectedRepOpps.length} open deal{selectedRepOpps.length === 1 ? "" : "s"} · click a deal to open it</div>
                </div>
                <button className="btn btn-sm" onClick={() => setSelectedRep(null)}>Close</button>
              </div>
              <ForecastDealsTable
                rows={selectedRepOpps}
                navigate={navigate}
                emptyText="No open opportunities for this rep."
              />
            </div>
          )}
        </>
      )}

      {tab === "deals" && (
        <div className="card">
          <ForecastDealsTable rows={openOpps} navigate={navigate} />
        </div>
      )}
    </>
  );
}

window.ForecastPage = ForecastPage;
